import { doc, updateDoc, deleteField, increment, arrayUnion, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebaseService';
import type { CampingSpot, CurrentCheckIn } from '../types';

const FRICAMP_NIGHTS_THRESHOLD = 3;

const CHECKOUT_HOUR = 12;
const DAY_MS = 24 * 60 * 60 * 1000;

export function calculateOccupancyUntil(days: number, fromMs: number = Date.now()): number {
  const base = new Date(fromMs);
  base.setHours(CHECKOUT_HOUR, 0, 0, 0);
  base.setDate(base.getDate() + Math.max(1, days));
  return base.getTime();
}

export function calculateRasteplassOccupancyUntil(fromMs: number = Date.now()): number {
  const now = new Date(fromMs);
  if (now.getHours() < 18) {
    return fromMs + 2 * 60 * 60 * 1000; // 2 hours
  }
  const tomorrow = new Date(fromMs);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  return tomorrow.getTime();
}

export async function markSpotOccupied(
  spotId: string,
  userId: string,
  userName: string,
  days: number,
  options?: { countAsNewCheckIn?: boolean; explicitOccupiedUntil?: number; alreadyVerified?: boolean; userPhoto?: string | null }
): Promise<number> {
  const occupiedUntil = options?.explicitOccupiedUntil || calculateOccupancyUntil(days);
  const countAsNewCheckIn = options?.countAsNewCheckIn !== false;
  const now = Date.now();
  const update = {
    occupiedBy: userId,
    occupiedByName: userName,
    occupiedUntil,
    ...(countAsNewCheckIn
      ? {
          checkInCount: increment(1),
          checkInNights: increment(Math.max(1, days)),
          lastCheckInAt: now,
          // En ekte innsjekk bekrefter plassen: en gjest har faktisk stått her.
          isVerified: true,
          lastConfirmedAt: now,
          // Denormaliser bekrefteren → feed-avataren viser hvem som bekreftet.
          confirmedBy: userId,
          confirmedByName: userName,
          confirmedByPhoto: options?.userPhoto || null,
          // Behold første verifiseringstidspunkt hvis plassen alt var bekreftet,
          // så den ikke hopper til topps i "Nye steder" ved hver nye innsjekk.
          ...(options?.alreadyVerified ? {} : { verifiedAt: now }),
        }
      : {
          checkInNights: increment(Math.max(1, days) - 1),
        }),
  };
  const spotRef = doc(db, 'cached_spots', spotId);
  await updateDoc(spotRef, update);
  try {
    const mainSpotRef = doc(db, 'spots', spotId);
    await updateDoc(mainSpotRef, update);
  } catch {
    // Main spot doc may not exist for all spot types.
  }
  promoteToFricampIfNeeded(spotId).catch(() => {});
  return occupiedUntil;
}

/**
 * Forleng et pågående opphold med `extraDays` netter, regnet fra i dag.
 * Dette er SAMME opphold som den opprinnelige innsjekken — det teller derfor
 * ikke som en ny innsjekk (`checkInCount` og verifiseringstidspunkt røres ikke),
 * men legger til netter og flytter `occupiedUntil` fram. Skrives til både
 * `cached_spots` og `spots`. Returnerer ny `occupiedUntil`.
 */
export async function extendStay(spotId: string, extraDays: number): Promise<number> {
  const days = Math.max(1, Math.round(extraDays));
  const occupiedUntil = calculateOccupancyUntil(days);
  const update = {
    occupiedUntil,
    checkInNights: increment(days),
    lastCheckInAt: Date.now(),
  };
  const spotRef = doc(db, 'cached_spots', spotId);
  await updateDoc(spotRef, update);
  try {
    await updateDoc(doc(db, 'spots', spotId), update);
  } catch {
    // Main spot doc may not exist for all spot types.
  }
  promoteToFricampIfNeeded(spotId).catch(() => {});
  return occupiedUntil;
}

async function promoteToFricampIfNeeded(spotId: string): Promise<void> {
  const spotRef = doc(db, 'cached_spots', spotId);
  const snap = await getDoc(spotRef);
  if (!snap.exists()) return;
  const data = snap.data();
  if (data.spotType !== 'rasteplass') return;
  const features: string[] = Array.isArray(data.features) ? data.features : [];
  if (features.includes('FRICAMP')) return;
  if ((data.checkInNights ?? 0) < FRICAMP_NIGHTS_THRESHOLD) return;
  await updateDoc(spotRef, { features: arrayUnion('FRICAMP') });
  try {
    await updateDoc(doc(db, 'spots', spotId), { features: arrayUnion('FRICAMP') });
  } catch {}
}

export async function clearSpotOccupancy(spotId: string): Promise<void> {
  const spotRef = doc(db, 'cached_spots', spotId);
  try {
    await updateDoc(spotRef, {
      occupiedBy: deleteField(),
      occupiedByName: deleteField(),
      occupiedUntil: deleteField(),
    });
  } catch (e: any) {
    // `updateDoc` kaster «not-found» hvis cached_spots-dokumentet er borte (f.eks.
    // deduplisert/ryddet etter at brukeren sjekket inn). Å rydde opptatt-status på
    // et dokument som ikke finnes er per definisjon en no-op — svelg den. Ellers
    // ville utsjekken strande: innsjekk-pekeren blir aldri fjernet og pillen
    // forsvinner ikke.
    if (e?.code !== 'not-found') throw e;
  }
}

export function isSpotOccupied(spot: Pick<CampingSpot, 'occupiedUntil'>): boolean {
  return !!spot.occupiedUntil && spot.occupiedUntil > Date.now();
}

/**
 * Avgjør hva app-start-banneret skal gjøre for nærmeste innsjekkbare plass.
 *
 *  - `defer`   : brukerdokumentet er ikke lastet ennå (foreløpig auth-callback,
 *                `_firestoreReady=false`). Da mangler `currentCheckIn`, så vi vet
 *                IKKE om brukeren alt er sjekket inn (også på tvers av enheter) —
 *                utsett avgjørelsen i stedet for å mase om innsjekk feilaktig.
 *  - `extend`  : brukeren er sjekket inn HER og oppholdet utløper snart (<24 t) →
 *                tilby forlengelse.
 *  - `checkin` : ingen aktiv innsjekk her og plassen er ledig → tilby innsjekk.
 *  - `none`    : gjør ingenting (ingen plass nær, nylig avvist, alt innsjekket
 *                med god tid igjen, eller plassen er opptatt av noen andre).
 *
 * Ren beslutningslogikk — kalleren står for å finne nærmeste plass, lese
 * avvist-status og faktisk vise banneret / låse «kun én gang»-ref-en.
 */
export function evaluateAutoCheckIn(params: {
  firestoreReady: boolean;
  hasNearbySpot: boolean;
  nearbyDismissed: boolean;
  nearbySpotId?: string;
  nearbyOccupiedUntil?: number;
  currentCheckIn?: { spotId?: string; occupiedUntil?: number } | null;
  now?: number;
}): 'defer' | 'none' | 'checkin' | 'extend' {
  const now = params.now ?? Date.now();
  if (!params.firestoreReady) return 'defer';
  if (!params.hasNearbySpot || params.nearbyDismissed) return 'none';
  const ci = params.currentCheckIn;
  if (ci?.spotId && ci.spotId === params.nearbySpotId) {
    const until = params.nearbyOccupiedUntil ?? ci.occupiedUntil ?? 0;
    return until - now < DAY_MS ? 'extend' : 'none';
  }
  const occupied = !!params.nearbyOccupiedUntil && params.nearbyOccupiedUntil > now;
  return occupied ? 'none' : 'checkin';
}

/** Lagre hvilken plass brukeren er sjekket inn på nå (peker på brukerdokumentet). */
export async function setCurrentCheckIn(userId: string, checkIn: CurrentCheckIn): Promise<void> {
  await setDoc(doc(db, 'users', userId), { currentCheckIn: checkIn }, { merge: true });
}

/** Fjern innsjekk-pekeren på brukerdokumentet (ved utsjekk). */
export async function clearCurrentCheckIn(userId: string): Promise<void> {
  await setDoc(doc(db, 'users', userId), { currentCheckIn: deleteField() }, { merge: true });
}

/**
 * Auto-utsjekk: rydder opp opptatt-status på den forrige plassen brukeren var sjekket
 * inn på, når hen sjekker inn på en ny. No-op hvis det ikke fantes en forrige innsjekk,
 * eller hvis det er samme plass (re-innsjekk / forlengelse). Returnerer den forrige
 * innsjekken når en auto-utsjekk faktisk ble utført, ellers null — slik at kalleren kan
 * oppdatere lokal UI og logge utsjekktidspunkt.
 */
export async function autoCheckOutPrevious(
  previous: CurrentCheckIn | undefined | null,
  newSpotId: string
): Promise<CurrentCheckIn | null> {
  if (!previous || !previous.spotId || previous.spotId === newSpotId) return null;
  await clearSpotOccupancy(previous.spotId);
  return previous;
}

export function occupancyDaysRemaining(occupiedUntil: number): number {
  const now = new Date();
  const end = new Date(occupiedUntil);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(0, Math.round((endDay.getTime() - today.getTime()) / DAY_MS));
}

export function occupancyEndDate(occupiedUntil: number, locale: string = 'nb-NO'): string {
  return new Date(occupiedUntil).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
}
